import {Component, Element, h, Host, Listen, Prop, State} from '@stencil/core';
import {getCookie, getCookieValue} from '../../utils/cookie';
import {DataService} from '../../utils/data-service.service';
import {isPlatform} from '@baloise/ds-core';

@Component({
  tag: 'bam-helpbox',
  styleUrl: 'bam-helpbox.scss',
  shadow: false,
})
export class BamHelpbox {
  @Element() el: HTMLElement;
  @Prop() ajaxUrl?: string;
  @Prop() disableConsultant: boolean = false;
  @Prop() generalPhone?: string;
  @Prop() generalText?: string;
  @Prop() email?: string;
  @Prop() bookingLink?: string;
  @Prop() bookingLabel?: string;
  @Prop() buttonLabel?: string;
  @Prop() buttonCloseLabel?: string;
  @Prop() directLink?: string;
  @Prop() showContactIconMobile?: boolean = false;
  @State() isHelpboxHidden = true;
  @State() isDesktop = isPlatform('desktop');
  @State() isMobile = isPlatform('mobile');
  private hasConsultant: boolean = false;
  private consultantTitle?: string;
  private bamNotificationsWrapper: HTMLElement;
  private cookieId: string = 'BaloiseRecentConsultantId';
  private cookieValue: string;
  private consultantMarkup: any;
  private focusableElements: NodeListOf<HTMLAnchorElement>;
  private overlayTrigger: HTMLButtonElement;

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.isDesktop = isPlatform('desktop');
    this.isMobile = isPlatform('mobile');
  }

  toggleHelpbox = async event => {
    if (this.cookieValue !== getCookie(this.cookieId)) {
      await this.getConsultantData();
    }
    this.isHelpboxHidden = !this.isHelpboxHidden;
    this.bamNotificationsWrapper.setAttribute('open', `${!this.isHelpboxHidden}`);
    this.overlayTrigger.setAttribute('aria-expanded', `${!this.isHelpboxHidden}`);
    if (!this.isHelpboxHidden) {
      this.el.addEventListener('keydown', this.trapFocus);
    } else {
      this.overlayTrigger.focus();
      this.el.removeEventListener('keydown', this.trapFocus);
    }
    window.track(event);
  };

  trapFocus = event => {
    const firstFocusableElement = this.focusableElements[0];
    const lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];

    if (event.key !== 'Tab') {
      return;
    }
    if (event.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
      }
    } else {
      if (document.activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    }
  };

  async getConsultantData() {
    if (this.ajaxUrl && !this.disableConsultant && getCookie(this.cookieId)) {
      this.cookieValue = getCookieValue(this.cookieId);
      await DataService.getData(this.ajaxUrl + this.cookieValue).then(data => {
        if (data) {
          this.hasConsultant = true;
          this.consultantTitle = data.title;
          this.consultantMarkup = this.setConsultantMarkup(data);
        }
      });
    }
  }

  async componentDidLoad() {
    await this.getConsultantData();
    this.bamNotificationsWrapper = this.el.closest('bam-notifications-wrapper');
    if (this.directLink) {
      this.bamNotificationsWrapper.querySelector('.cta-wrapper').classList.add('has-direct-link');
    }
    this.focusableElements = this.el.querySelectorAll('a, button');
    this.overlayTrigger = this.el.querySelector('bal-button').querySelector('button');
    this.overlayTrigger?.setAttribute('aria-expanded', 'false');
  }

  setConsultantMarkup(data: any) {
    let image = data.image.src.replace('{ratio}', '1-1');
    if (image.includes('avatars')) {
      image = image.replace('.svg', '_1-1.svg');
    }
    return (
      <div class="helpbox__contact-wrapper">
        <div class="grid is-multiline mb-normal">
          <div class="col is-narrow pr-x-small pb-none flex">
            <img src={image} alt={data.image.alt} class="helpbox__contact-image" />
          </div>
          <div class="mt-normal pt-none col mobile:is-full">
            <bal-text size="small" class="text-x-small" space="none">
              {data.info}
            </bal-text>
            {data.link?.href ? (
              <a href={data.link.href} data-tracking-style="cta" data-tracking-topic="Kontaktinformation" onClick={event => window.track(event)} class="consultant-link">
                <bal-heading level="h5" visual-level="h4" space="none">
                  {data.title}
                </bal-heading>
              </a>
            ) : (
              <bal-heading level="h5" visual-level="h4" space="none">
                {data.title}
              </bal-heading>
            )}
          </div>
        </div>
        {data.phone?.value && (
          <div class="pb-normal">
            <bal-button
              flat={true}
              color="link"
              icon="call"
              href={`tel:${data.phone.value}`}
              data-tracking-style="cta"
              data-tracking-topic={data.phone.value}
              onClick={event => window.track(event)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {data.phone.value}
            </bal-button>
          </div>
        )}
        {data.mobile?.value && (
          <div class="pb-normal">
            <bal-button
              flat={true}
              color="link"
              icon="mobile"
              href={`tel:${data.mobile.value}`}
              data-tracking-style="cta"
              data-tracking-topic={data.mobile.value}
              onClick={event => window.track(event)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {data.mobile.value}
            </bal-button>
          </div>
        )}
        {data.mail?.value && (
          <div class="pb-normal">
            <bal-button
              flat={true}
              color="link"
              icon="message"
              href={`mailto:${data.mail.value}`}
              data-tracking-style="cta"
              data-tracking-topic={data.mail.label}
              onClick={event => window.track(event)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {data.mail.label}
            </bal-button>
          </div>
        )}
        {data.appointment?.label && (
          <bal-button
            color="primary"
            href={data.appointment.href}
            data-tracking-style="cta"
            data-tracking-topic={data.appointment.label}
            onClick={event => window.track(event)}
            class="mt-small"
          >
            {data.appointment.label}
          </bal-button>
        )}
      </div>
    );
  }

  getGeneralMarkup() {
    const contactWrapperClass = this.generalPhone || this.email ? 'helpbox__contact-wrapper my-small' : 'helpbox__contact-wrapper is-empty';

    return (
      <div class={contactWrapperClass}>
        {this.generalPhone && (
          <bal-button
            class="pb-small"
            flat={true}
            color="link"
            icon="call"
            data-tracking-style="cta"
            data-tracking-topic={this.generalPhone}
            onClick={event => window.track(event)}
            href={`tel:${this.generalPhone}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {this.generalPhone}
          </bal-button>
        )}
        <bal-text size="small" class="phone-text">
          {this.generalText}
        </bal-text>
        {this.bookingLabel && this.bookingLink && (
          <bal-button
            class="mt-normal"
            flat={true}
            color="link"
            icon="date"
            href={this.bookingLink}
            data-tracking-style="cta"
            data-tracking-topic={this.bookingLabel}
            onClick={event => window.track(event)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {this.bookingLabel}
          </bal-button>
        )}
        {this.email && (
          <bal-button
            class="mt-normal"
            flat={true}
            color="link"
            icon="message"
            href={`mailto:${this.email}`}
            data-tracking-style="cta"
            data-tracking-topic={this.email}
            onClick={event => window.track(event)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {this.email}
          </bal-button>
        )}
      </div>
    );
  }

  render() {
    const { hasConsultant, consultantTitle, buttonLabel, buttonCloseLabel } = this;

    const helpboxClass = hasConsultant ? 'is-consultant' : this.directLink ? 'ml-xxx-large' : '';
    const closeLabel = !this.isMobile ? (this.isHelpboxHidden ? buttonLabel : buttonCloseLabel) : this.isHelpboxHidden ? '' : buttonCloseLabel;
    const icon = !this.isMobile ? (this.isHelpboxHidden ? '' : 'close') : this.isHelpboxHidden ? (this.showContactIconMobile ? 'contact' : 'account') : 'close';

    return (
      <Host class={helpboxClass} data-tracking-context="helpbox" data-tracking-id={`${consultantTitle === undefined ? 'Kein Berater' : consultantTitle}#helpbox`}>
        {this.directLink ? (
          <bal-button
            href={this.directLink}
            class={{ 'mt-x-small': this.isMobile, 'text-small': buttonLabel.length >= 20 }}
            color="info"
            data-tracking-style="cta"
            data-tracking-topic={this.directLink}
            onClick={event => window.track(event)}
          >
            {buttonLabel}
          </bal-button>
        ) : (
          <div>
            <div
              onClick={event => this.toggleHelpbox(event)}
              class={{
                'helpbox__backdrop': true,
                'hidden': this.isHelpboxHidden,
              }}
            />
            <bal-button
              onClick={event => this.toggleHelpbox(event)}
              class={{ 'mt-x-small': this.isMobile }}
              color="info"
              data-tracking-style="cta"
              data-tracking-topic="Kontaktinformation"
              icon={icon}
              square={this.isMobile && this.isHelpboxHidden}
              aria-expanded={!this.isHelpboxHidden}
              aria-controls="helpbox-content"
            >
              {closeLabel}
            </bal-button>

            <div
              id="helpbox-content"
              class={{
                'helpbox__content-wrapper': true,
                'is-hide': this.isHelpboxHidden,
              }}
              aria-hidden={this.isHelpboxHidden}
            >
              {hasConsultant ? this.consultantMarkup : this.getGeneralMarkup()}
              <slot name="linklist">
                <div class="helpbox__linklist-wrapper"></div>
              </slot>
            </div>
          </div>
        )}
      </Host>
    );
  }
}
